import React, { Component } from 'react';
// Composants
import { Button, Dimmer, Header, Loader, Progress, TextArea } from 'semantic-ui-react';
import UseAnimations from 'react-useanimations';
// Librairies
import i18n from '../../locales/i18n';
import { connect } from 'react-redux';
import alert from 'react-useanimations/lib/alertCircle';
import settings from 'react-useanimations/lib/settings';
import Bowser from 'bowser';
import { isMobileOnly } from 'react-device-detect';
// Services
import ErrorsService from '../../services/ErrorsService';
// Utils
import StylesUtil from '../../utils/StylesUtil';
import { applyUpdate } from '../../serviceWorker';

class ErrorBoundary extends Component {
    state = {
        hasError: false,
        isUnderMaintenance: false,
        secondsUntilRefresh: -1,
        errorMessage: null,
        isUpdateStalled: false
    };

    render() {
        const { updateApp, isGlobal } = this.props;
        const { hasError, isUnderMaintenance, secondsUntilRefresh, errorMessage, isUpdateStalled } = this.state;

        if (hasError) {
            return (
                <div id='error-handler'>
                    <Header as='h1' icon textAlign='center'>
                        {updateApp &&
                            <Dimmer active style={StylesUtil.getMapStyles().dimmerStyle}>
                                <Loader content={
                                    <>
                                        <Progress value={2} total={4} size='tiny' style={isMobileOnly ? { width: '200px' } : { width: '400px' }}>
                                            <div style={{ color: 'white' }}>{i18n.t("Mise à jour de l'application...")}</div>
                                        </Progress>
                                        {isUpdateStalled && <Button size='small' content={i18n.t("Rafraîchir la page")} onClick={this.retryUpdate} />}
                                    </>
                                } />
                            </Dimmer>}
                        {isUnderMaintenance &&
                            <>
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                    <UseAnimations animation={settings} strokeColor='white' autoplay loop size={130} />
                                </div>
                                <Header.Content style={{ color: 'white' }}>{i18n.t("L'application est en cours de maintenance...")}</Header.Content>
                                <Header.Subheader as='h3' style={{ color: 'white' }}>{i18n.t("Une nouvelle version sera bientôt disponible.")}</Header.Subheader>
                            </>}
                        {!updateApp && !isUnderMaintenance &&
                            <>
                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                    <UseAnimations animation={alert} strokeColor='white' autoplay loop size={130} />
                                </div>
                                <Header.Content style={{ color: 'white' }}>
                                    {isGlobal
                                        ? i18n.t("Oops ! Une erreur est survenue...")
                                        : i18n.t("Oops ! Une erreur est survenue sur cette page...")}
                                </Header.Content>
                                <TextArea className='text-area--error'>
                                    {errorMessage}
                                </TextArea>
                                <Header.Subheader as='h3' style={{ color: 'white' }}>{i18n.t("Le problème a été remonté à l'équipe technique.")}</Header.Subheader>
                                {!isGlobal && <Header.Subheader as='h3' style={{ color: 'white' }}>{i18n.t("Vous pouvez continuer votre navigation dans le reste de l'application.")}</Header.Subheader>}
                                {isGlobal &&
                                    <Header.Content style={{ marginTop: '5px' }} >
                                        <Button content={`${i18n.t("Rafraîchir la page")} (${secondsUntilRefresh})`} onClick={() => window.location.reload()} />
                                    </Header.Content>}
                            </>}
                    </Header>
                </div>
            );
        }

        return this.props.children;
    }

    componentDidCatch = (error, errorInfo) => {
        const changeCountdown = () => {
            this.setState(prevState => ({ secondsUntilRefresh: prevState.secondsUntilRefresh - 1 }), () => {
                if (this.state.secondsUntilRefresh <= 0) window.location.reload();
                else setTimeout(changeCountdown, 1000);
            });
        };

        if (!this.triggeredCountdown) {
            this.triggeredCountdown = true;
            this.setState({ hasError: true, isUnderMaintenance: error.message === 'isUnderMaintenance' });

            if (error.message !== 'isUnderMaintenance') {
                if (this.props.updateApp) this.updateApplication();

                if (!this.props.updateApp) {
                    const bowser = Bowser.parse(window.navigator.userAgent);
                    ErrorsService.addError({
                        projectId: this.props.project?.id || null,
                        date: new Date().toUTCString(),
                        message: error.message + errorInfo.componentStack,
                        device: `${bowser?.platform?.vendor || ''} ${bowser?.platform?.type || ''}`.trim(),
                        os: `${bowser?.os?.name || ''} ${bowser?.os?.versionName || ''} ${bowser?.os?.version || ''}`.trim(),
                        browser: `${bowser?.browser?.name || ''} ${bowser?.browser?.version || ''}`.trim(),
                        language: navigator.language || navigator.userLanguage,
                        url: window.location.href,
                        connectionSpeed: navigator.connection?.effectiveType
                    });

                    this.setState({ secondsUntilRefresh: 60, errorMessage: error.message }, () => {
                        if (this.props.isGlobal) setTimeout(changeCountdown, 1000);
                    });
                }
            }
        }
    };

    componentWillUnmount = () => clearInterval(this.updatePolling);

    // Une seule tentative automatique par session ; ensuite, nouvel essai toutes les 30 s sans recharger en boucle
    updateApplication = async () => {
        const registration = await navigator.serviceWorker?.getRegistration?.().catch(() => null);
        if (sessionStorage.getItem('grality-update-attempted')) {
            this.setState({ isUpdateStalled: true });
            if (registration) this.updatePolling = setInterval(() => {
                registration.update().catch(() => { }).then(() => {
                    if (registration.waiting) applyUpdate(registration);
                });
            }, 30000);
            return;
        }

        sessionStorage.setItem('grality-update-attempted', 'true');
        if (!registration) {
            window.location.reload();
            return;
        }
        await registration.update().catch(() => { });
        if (registration.waiting) applyUpdate(registration);
        else if (registration.installing)
            registration.installing.addEventListener('statechange', () => {
                if (registration.waiting) applyUpdate(registration);
            });
        else window.location.reload();
    }

    retryUpdate = () => {
        sessionStorage.removeItem('grality-update-attempted');
        window.location.reload();
    }
}

const mapStateToProps = (state) => {
    return {
        updateApp: state.updateApp,
        project: state.project
    };
};

export default connect(mapStateToProps)(ErrorBoundary);